import React, { useEffect, useRef, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";

/**
 * Rich text for Station Portal broadcast body (stored as HTML in system-config).
 * Matches TinyMCE setup used by AdminContentManage; toolbar tuned for email-like formatting (fonts, colors, lists).
 */
export default function StationPortalBroadcastRichTextEditor(props: {
	value: string;
	onChange: (html: string) => void;
	disabled?: boolean;
}) {
	const editorRef = useRef<any>(null);
	const [isEditorReady, setIsEditorReady] = useState(false);

	useEffect(() => {
		const ed = editorRef.current;
		if (!ed || typeof ed.mode?.set !== "function") return;
		try {
			ed.mode.set(props.disabled ? "readonly" : "design");
		} catch {
			/* ignore */
		}
	}, [props.disabled, isEditorReady]);

	useEffect(() => {
		let cancelled = false;
		if (typeof window === "undefined") return;

		(async () => {
			try {
				await import("tinymce/tinymce");
				await import("tinymce/icons/default");
				await import("tinymce/themes/silver");
				await import("tinymce/models/dom");

				await import("tinymce/plugins/advlist");
				await import("tinymce/plugins/autolink");
				await import("tinymce/plugins/lists");
				await import("tinymce/plugins/link");
				await import("tinymce/plugins/charmap");
				await import("tinymce/plugins/searchreplace");
				await import("tinymce/plugins/visualblocks");
				await import("tinymce/plugins/code");
				await import("tinymce/plugins/fullscreen");
				await import("tinymce/plugins/wordcount");

				// CSS imports are best-effort — skin: false / content_css: false mean
				// TinyMCE won't try to fetch them itself, but we inject them for styling.
				// If the chunk fails (e.g. in some prod/rspack configs) the editor still
				// renders; it just won't have the oxide skin style.
				try {
					await import("tinymce/skins/ui/oxide/skin.min.css");
					await import("tinymce/skins/content/default/content.min.css");
				} catch {
					/* non-fatal: editor renders without oxide skin */
				}

				if (!cancelled) setIsEditorReady(true);
			} catch (err) {
				console.error("[StationBroadcast] TinyMCE load failed:", err);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, []);

	return (
		<div id="whl-station-broadcast-tinymce-root">
			{isEditorReady ? (
				<Editor
					licenseKey="gpl"
					onInit={(_evt: unknown, editor: any) => {
						editorRef.current = editor;
					}}
					value={props.value}
					onEditorChange={(next: string) => props.onChange(next)}
					init={{
						height: 380,
						ui_container: "#whl-station-broadcast-tinymce-root",
						menubar: "edit format",
						toolbar_mode: "wrap",
						readonly: !!props.disabled,
						plugins: "advlist autolink lists link charmap searchreplace visualblocks code fullscreen wordcount",
						toolbar:
							"undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist | outdent indent | link unlink | removeformat | code fullscreen",
						skin: false,
						content_css: false,
						content_style:
							"body { font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; font-size: 15px; line-height: 1.6; }",
					}}
				/>
			) : (
				<div style={{ padding: 16, border: "1px solid #e5e7eb", borderRadius: 8, color: "#6b7280" }}>
					Đang tải trình soạn thảo…
				</div>
			)}
		</div>
	);
}
